import { createContext, useContext, useState, useEffect } from "react";
import { watchAuthState, signOutUser } from "../services/auth";
import { getUser } from "../services/userData";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [firebaseUser, setFirebaseUser] = useState(null);
  const [userData, setUserData]         = useState(null);
  const [loading, setLoading]           = useState(true);

  // Watch Firebase auth state — fires on login, logout, and page reload
  useEffect(() => {
    const unsub = watchAuthState(async (fbUser) => {
      setFirebaseUser(fbUser);
      if (fbUser) {
        try {
          const data = await getUser({ email: fbUser.email });
          setUserData(data);
        } catch (err) {
          console.warn("Failed to load user data:", err.message);
          setUserData(null);
        }
      } else {
        setUserData(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const logout = async () => {
    await signOutUser();
  };

  // Force-refresh user data (called after a new purchase)
  const refreshUserData = async () => {
    if (!firebaseUser) return;
    const data = await getUser({ email: firebaseUser.email });
    setUserData(data);
  };

  // The "user" exposed to the rest of the app is a merged object
  const user = firebaseUser
    ? {
        email: firebaseUser.email,
        emailVerified: firebaseUser.emailVerified,
        ...(userData || {}),
      }
    : null;

  return (
    <AuthContext.Provider value={{ user, loading, logout, refreshUserData }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
